import { NoSsr } from "@mui/base";
import { theme, useResponsive } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { sortArrayByReferenceOrder } from "~/lib/utils";
import type { genericArticleFooterArticle$key } from "~/queries/__generated__/genericArticleFooterArticle.graphql";
import type { genericArticleFooterQuery } from "~/queries/__generated__/genericArticleFooterQuery.graphql";

import type { Props as GenericArticleFooterItemProps } from "./generic-article-footer-item";
import { GenericArticleFooterItem } from "./generic-article-footer-item";
import type { ArticleItem } from "./hooks";
import { useDesktopRows, useTracking } from "./hooks";
import { Container, Grid, Header, Row } from "./styles";

export type Props = {
  articleIds: string[];
  className?: string;
  recommendationId: string;
  reference: genericArticleFooterArticle$key;
};

export const GenericArticleFooter: FunctionComponent<Props> = ({
  articleIds,
  className,
  recommendationId,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleFooterArticle on Article {
        entityId
      }
    `,
    reference_,
  );

  const { trackClick, trackImpression } = useTracking();
  const data = useLazyLoadQuery<genericArticleFooterQuery>(
    graphql`
      query genericArticleFooterQuery($entityIds: [String], $first: Int!) {
        articles(filter: { entityIds: $entityIds }, first: $first) {
          edges {
            node {
              ...genericArticleFooterItemArticle
              entityId
            }
          }
        }
      }
    `,
    {
      entityIds: articleIds,
      first: articleIds.length,
    },
    { fetchPolicy: "store-or-network" },
  );

  const orderedArticles = sortArrayByReferenceOrder({
    data: { array: data.articles.edges ?? [], idGetter: item => item.node.entityId },
    order: { array: articleIds, idGetter: item => item },
  });

  const rows = useDesktopRows(orderedArticles);

  useEffect(() => {
    if (orderedArticles.length === 0) return;
    trackImpression(
      recommendationId,
      orderedArticles.map(({ node }) => node.entityId),
    );
  }, [recommendationId, orderedArticles, trackImpression]);

  const renderItem = (item: ArticleItem, size: GenericArticleFooterItemProps["size"]) => (
    <GenericArticleFooterItem
      key={item.node.entityId}
      query={{
        campaign: article.entityId,
        module: `bottom_card_${item.index}`,
        pgtype: "article",
      }}
      reference={item.node}
      size={size}
      onClick={() => trackClick(recommendationId, item.node.entityId)}
    />
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const shouldShow = useMemo(
    () => (isMobile ? orderedArticles.length > 0 : rows.length > 0),
    [orderedArticles.length, isMobile, rows.length],
  );

  return (
    <NoSsr>
      {shouldShow ? (
        <Container className={className}>
          <Header>More On This Topic</Header>
          <Grid>
            {isMobile
              ? orderedArticles.map((item, index) => {
                  const size = index % 5 === 0 ? "large" : "normal";
                  return renderItem({ ...item, index: index + 1 }, size);
                })
              : rows.map((row, index) => (
                  <Row $columns={row.items.length} key={index}>
                    {row.items.map(item => {
                      const size = index === 0 ? "large" : "normal";
                      return renderItem(item, size);
                    })}
                  </Row>
                ))}
          </Grid>
        </Container>
      ) : null}
    </NoSsr>
  );
};

GenericArticleFooter.displayName = "GenericArticleFooter";
